import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faChevronLeft,
  faChevronRight,
  faArrowLeft,
  faArrowRight,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Row, Col, Table, Badge, Form, InputGroup, Button } from "@themesberg/react-bootstrap";
import { adminGetBonusHistory, adminGetBonusCycles, adminGetBonusSlabs } from "../../../pages/api";
import { useNavigate, Link } from "react-router-dom";
import { Routes } from "../../../routes";

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getAdminEligibleUsersPath = () => {
  return Routes?.AdminEligibleUsers?.path || "/admin-eligible-users";
};

const PROCESSING_STATUS_OPTIONS = [
  { value: "draft", label: "Draft" },
  { value: "ready to process", label: "Ready to Process" },
  { value: "processed", label: "Processed" },
  { value: "archived", label: "Archived" },
];

const badgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (["processed", "sent", "eligible", "active", "ready to process"].includes(s)) return "success";
  if (["pending", "draft"].includes(s)) return "warning";
  if (["failed", "inactive", "not eligible", "already claimed", "archived"].includes(s)) return "danger";
  return "secondary";
};

const getPageItems = (currentPage, totalPages) => {
  if (totalPages <= 5) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (currentPage <= 2) return [1, 2, 3, "...", totalPages];
  if (currentPage >= totalPages - 1) return [1, "...", totalPages - 2, totalPages - 1, totalPages];
  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

const getShowingRange = (pagination, currentRowsCount = 0) => {
  const total = Number(pagination.total || 0);
  const current = Number(pagination.current_page || 1);
  const perPage = Number(pagination.per_page || 10);

  if (total === 0 || currentRowsCount === 0) return { start: 0, end: 0 };

  let start = (current - 1) * perPage + 1;
  let end = Math.min(start + currentRowsCount - 1, total);

  if (start > total) {
    start = total;
    end = total;
  }

  if (end < start) {
    end = start;
  }

  return { start, end };
};

const mapCycleOption = (item) => ({
  id: item?.id ?? Date.now(),
  cycle_name: item?.cycle_name || "",
  cycle_month: item?.cycle_month || "",
});

const mapSlabOption = (item) => ({
  id: item?.id ?? Date.now(),
  slab_name: item?.slab_name || "",
  threshold: item?.threshold ?? "",
});

const mapHistoryRow = (item) => ({
  id: item?.id ?? Date.now(),
  slab: item?.slab || "N/A" || "-",
  cycle: item?.cycle || "N/A" || "-",
  user: item?.user || "N/A" || "-",
  processing_status: item?.processing_status || "N/A" || "-",
  followers_count: item?.followers_count ?? 0,
  amount: item?.amount ?? 0,
  remark: item?.remark || "N/A" || "-",
  created_at: item?.created_at || "N/A" || "",
});

const capitalize = (value) =>
  String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";

  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};


const formatCurrency = (value) => {
  const num = Number(value ?? 0);
  if (Number.isNaN(num)) return "0.00";
  return num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

export default function AdminBonusHistory() {
  const navigate = useNavigate();
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [loadingOptions, setLoadingOptions] = useState(false);

  const [cycle, setCycle] = useState("");
  const [userId, setUserId] = useState("");
  const [slab, setSlab] = useState("");
  const [processingStatus, setProcessingStatus] = useState("");
  const [search, setSearch] = useState("");

  const [history, setHistory] = useState([]);
  const [cycleOptions, setCycleOptions] = useState([]);
  const [slabOptions, setSlabOptions] = useState([]);

  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const initialLoadRef = useRef(true);
  const searchDebounceRef = useRef(null);

  const clearSearchDebounce = () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
      searchDebounceRef.current = null;
    }
  };

  const fetchCycleOptions = async () => {
    try {
      const response = await adminGetBonusCycles({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCycles = Array.isArray(apiData?.cycles) ? apiData.cycles : [];
      setCycleOptions(apiCycles.map(mapCycleOption));
    } catch (error) {
      console.error("Failed to fetch cycle options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch cycle options.");
      setCycleOptions([]);
    }
  };

  const fetchSlabOptions = async () => {
    try {
      const response = await adminGetBonusSlabs({ pagination: false });
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiSlabs = Array.isArray(apiData?.slabs) ? apiData.slabs : [];
      setSlabOptions(apiSlabs.map(mapSlabOption));
    } catch (error) {
      console.error("Failed to fetch slab options:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch slab options.");
      setSlabOptions([]);
    }
  };

  const fetchHistory = async (pageNo = 1, perPage = 10, filters = {}) => {
    try {
      setLoadingHistory(true);

      const params = {
        page: pageNo,
        per_page: perPage,
      };

      if (filters.cycleId) params.cycle_id = filters.cycleId;
      if (filters.slabId) params.slab_id = filters.slabId;
      if (filters.processingStatus) params.processing_status = filters.processingStatus;

      const trimmedSearch = String(filters.search || "").trim();
      if (trimmedSearch) {
        params.search = trimmedSearch;
      }

      const response = await adminGetBonusHistory(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiHistory = Array.isArray(apiData?.history) ? apiData.history : [];
      const apiPagination = apiData?.pagination || {};

      setHistory(apiHistory.map(mapHistoryRow));
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiHistory.length,
      });
    } catch (error) {
      console.error("Failed to fetch bonus history:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch bonus history.");
      setHistory([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    (async () => {
      setLoadingOptions(true);
      try {
        await Promise.all([fetchCycleOptions(), fetchSlabOptions()]);
      } finally {
        setLoadingOptions(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const filters = {
      cycleId: cycle,
      slabId: slab,
      processingStatus,
      search,
    };

    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchHistory(1, 10, filters);
      return undefined;
    }

    clearSearchDebounce();
    const delay = String(search || "").trim() ? 300 : 0;

    searchDebounceRef.current = setTimeout(() => {
      fetchHistory(page, pagination.per_page || 10, filters);
    }, delay);

    return () => {
      clearSearchDebounce();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, cycle, slab, processingStatus, search]);

  const handleRefresh = async () => {
    setPage(1);
    clearSearchDebounce();

    await Promise.all([
      fetchCycleOptions(),
      fetchSlabOptions(),
      fetchHistory(1, pagination.per_page || 10, {
        cycleId: cycle,
        slabId: slab,
        processingStatus,
        search,
      }),
    ]);
  };

  const { start, end } = useMemo(() => getShowingRange(pagination, history.length), [pagination, history.length]);

  const getPageItemsList = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;
    return getPageItems(current, totalPages);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4 gap-2">
        <div>
          <h2 className="h4 mb-1">Bonus History</h2>
          {/* <div className="text-muted">Shows processed slabs, message log, and permanent claim history.</div> */}
        </div>

        <div className="d-flex justify-content-end ms-auto">
          <Button
            as={Link}
            to={getAdminEligibleUsersPath()}
            style={ACTIVE_BUTTON_STYLE}
          >
            <FontAwesomeIcon icon={faArrowRight} className="me-2" />
            Eligible Users
          </Button>
        </div>
      </div>

      <Card className="border-0 shadow-sm rounded-4 mb-4">
        <Card.Body className="p-4">
          <Row className="g-3">
            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">Cycle</Form.Label>
              <Form.Select
                value={cycle}
                onChange={(e) => {
                  setCycle(e.target.value);
                  setPage(1);
                }}
                disabled={loadingOptions}
              >
                <option value="">All Cycles</option>
                {cycleOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.cycle_name}
                    {item.cycle_month ? ` (${item.cycle_month})` : ""}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">Slab</Form.Label>
              <Form.Select
                value={slab}
                onChange={(e) => {
                  setSlab(e.target.value);
                  setPage(1);
                }}
                disabled={loadingOptions}
              >
                <option value="">All Slabs</option>
                {slabOptions.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.slab_name}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">Processed Status</Form.Label>
              <Form.Select
                value={processingStatus}
                onChange={(e) => {
                  setProcessingStatus(e.target.value);
                  setPage(1);
                }}
              >
                <option value="">All Status</option>
                {PROCESSING_STATUS_OPTIONS.map((item) => (
                  <option key={item.value} value={item.value}>
                    {item.label}
                  </option>
                ))}
              </Form.Select>
            </Col>

            <Col xs={12} md={6}>
              <Form.Label className="fw-semibold">Search</Form.Label>
              <InputGroup>
                <InputGroup.Text className="bg-white">
                  <FontAwesomeIcon icon={faSearch} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(1);
                  }}
                  placeholder="Search"
                />
              </InputGroup>
            </Col>
          </Row>

          {/* <div className="d-flex justify-content-between align-items-center mt-4 flex-wrap gap-2">
            <div className="text-muted small">Showing filter-based history records</div>
            <div className="d-flex gap-2 flex-wrap ms-auto">
              <Button type="button" className="btn btn-outline-primary course-page-btn" onClick={handleRefresh}>
                Refresh
              </Button>
            </div>
          </div> */}
        </Card.Body>
      </Card>

      <Card className="border-0 shadow-sm rounded-4">
        <Card.Body className="p-4">
          <div className="table-responsive">
            <Table hover className="align-middle mb-0 bonus-table">
              <thead>
                <tr>
                  <th>Cycle</th>
                  <th>User</th>
                  <th>Slab</th>
                  <th className="text-center">Followers</th>
                  <th className="text-center">Amount</th>
                  <th>Processing Status</th>
                  <th>Remark</th>
                  {/* <th>Created At</th> */}
                </tr>
              </thead>
              <tbody>
                {loadingHistory ? (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      Loading history...
                    </td>
                  </tr>
                ) : history.length > 0 ? (
                  history.map((item) => (
                    <tr key={item.id}>
                      <td className="fw-semibold">{item.cycle}</td>
                      <td>{item.user}</td>
                      <td>{item.slab}</td>
                      <td className="text-center">{formatNumber(item.followers_count)}</td>
                      <td className="text-center">{formatCurrency(item.amount)}</td>
                      <td>
                        <Badge bg={badgeVariant(item.processing_status)} className="rounded-pill px-3 py-2">
                          {capitalize(item.processing_status)}
                        </Badge>
                      </td>
                      <td>{item.remark || "N/A" || "-"}</td>
                      {/* <td>{formatDateTime(item.created_at)}</td> */}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-5">
                      No history records found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
            <div className="text-muted small">
              Showing {start} to {end} of {pagination.total} entries
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page <= 1 || loadingHistory}
                onClick={() => {
                  clearSearchDebounce();
                  fetchHistory(Math.max(1, pagination.current_page - 1), pagination.per_page, {
                    cycleId: cycle,
                    slabId: slab,
                    processingStatus,
                    search,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </Button>

              {getPageItemsList().map((item, index) =>
                item === "..." ? (
                  <span key={`dots-${index}`} className="text-muted px-1">
                    ...
                  </span>
                ) : (
                  <Button
                    key={item}
                    type="button"
                    variant="outline-primary"
                    disabled={loadingHistory}
                    onClick={() => {
                      clearSearchDebounce();
                      fetchHistory(item, pagination.per_page, {
                        cycleId: cycle,
                        slabId: slab,
                        processingStatus,
                        search,
                      });
                    }}
                    className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                  >
                    {item}
                  </Button>
                )
              )}

              <Button
                type="button"
                variant="outline-primary"
                disabled={pagination.current_page >= pagination.last_page || loadingHistory}
                onClick={() => {
                  clearSearchDebounce();
                  fetchHistory(Math.min(pagination.last_page, pagination.current_page + 1), pagination.per_page, {
                    cycleId: cycle,
                    slabId: slab,
                    processingStatus,
                    search,
                  });
                }}
                className="course-page-btn"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </Button>
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}